import { useCancelOrderApi } from 'apis/orders/use-cancel-order-api';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from 'design-system';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

const REASON_MAX_LENGTH = 500;

interface FormValues {
  reason: string;
}

interface CancelOrderModalProps {
  orderId: number;
  code: string;
  isOpen: boolean;
  onClose: () => void;
}

/** Ops cancel a placed or confirmed order; the reason becomes an order comment and the restaurant is notified. */
export const CancelOrderModal = ({ orderId, code, isOpen, onClose }: CancelOrderModalProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { reason: '' }, reValidateMode: 'onChange' });

  // Hook-level callbacks fire even if the modal unmounts under the screen's spinner while the details refetch.
  const { isLoading, mutate: cancelOrderApi } = useCancelOrderApi({
    onSuccess: () => {
      onClose();
      reset();
    },
    // The order moved under us: the hook has toasted and refetched; `onCloseComplete` resets the form.
    onError: (error) => {
      if (error.response?.status === 409) onClose();
    },
  });

  const onSubmit: SubmitHandler<FormValues> = ({ reason }) => {
    cancelOrderApi({ orderId, body: { reason: reason.trim() } });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={reset}
      size='lg'
      closeOnOverlayClick={!isLoading}
      closeOnEsc={!isLoading}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack align='start' spacing='xs'>
            <Heading fontSize='24px' fontWeight='semibold'>
              Cancel Order {code}
            </Heading>
            <Text color='gray.500' fontWeight='normal' fontSize='md'>
              The restaurant will be notified. Please give a reason.
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />

        <ModalBody>
          <form id='cancelOrderModalForm' onSubmit={handleSubmit(onSubmit)}>
            <FormControl isInvalid={!!errors.reason} isRequired>
              <FormLabel>Reason</FormLabel>
              <Controller
                name='reason'
                control={control}
                rules={{
                  validate: (value) => (value.trim().length > 0 ? true : 'A reason is required'),
                  maxLength: { value: REASON_MAX_LENGTH, message: `At most ${REASON_MAX_LENGTH} characters` },
                }}
                render={({ field }) => (
                  <Textarea {...field} placeholder='Why is this order being cancelled?' size='sm' />
                )}
              />
              <FormErrorMessage>{errors.reason?.message}</FormErrorMessage>
            </FormControl>
          </form>
        </ModalBody>

        <ModalFooter>
          <HStack spacing='sm'>
            <Button colorScheme='red' variant='outline' onClick={onClose} isDisabled={isLoading}>
              Back
            </Button>
            <Button type='submit' form='cancelOrderModalForm' colorScheme='red' variant='solid' isLoading={isLoading}>
              Cancel order
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
